import React, { Fragment, useEffect, useMemo, useState } from "react";

import { Line, List, Text } from "components";
import { FormInput } from "components/FormInput";
import Footer1 from "components/Footer1";
import InfoRibbon from "components/InfoRibbon";
import Header from "../../components/Header";
import { useLocation, useNavigate } from "react-router-dom";
import { formatCurrency, numberOrZero } from "../../helpers/number";
import { CartPreviewItem } from "../../components/CartPreviewItem";
import { toast } from "react-toastify";
import { scrollToTop } from "../../helpers/scroll";
import { clearSearchKeyword } from "../../states/search.mjs";
import { firstLetterUpper } from "../../helpers/sentence";
import { startLoading, stopLoading } from "../../states/progress.mjs";
import { submitOrder } from "../../service/api";
import { justArray } from "../../helpers/array";
import { PaymentOptions } from "../../components/PaymentOptions";
import { OrderPaidWidget } from "../../components/OrderPaidWidget";
import { PendingPaymentWidget } from "../../components/PendingPaymentWidget";
import { Order_Status } from "../../helpers/order";


function _amountInCarts(carts) {
  return Object
    .values(carts ?? {})
    .map(x => numberOrZero(x?.unit_price) * numberOrZero(x?.quantity))
    .reduce((a, b) => a + b, 0);
}

function _itemsInCarts(carts) {
  return Object
    .values(carts ?? {})
    .map(x => numberOrZero(x?.quantity))
    .reduce((a, b) => a + b, 0);
}

const OrderViewPage = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [orderStatus, setOrderStatus] = useState(Order_Status.NOT_PAID);
  const [pendingOrder, setPendingOrder] = useState();
  const [formData, setFormData] = useState({ mobile: state?.payment_reference });
  const [errors, setErrors] = useState();
  const [carts, setCarts] = useState([]);
  const [totalCartAmount, setTotalCartAmount] = useState(0);

  useEffect(() => {
    setOrderStatus(state?.paid === true ? Order_Status.PAID : Order_Status.NOT_PAID);
    setCarts(Object.values(state?.carts));
    setTotalCartAmount(_amountInCarts(state?.carts));
  }, [state]);

  useEffect(() => {
    if (!state) {
      navigate("/productspage");
    }
  }, [navigate, state]);

  useEffect(() => {
    scrollToTop();
    return () => {
      clearSearchKeyword();
    };
  }, []);

  const paymentSubmit = useMemo(() => {
    return () => {
      const req = {
        data: {
          carts: justArray(Object.values(state?.carts)).map((cart, index) => ({
            quantity: cart?.quantity,
            unit_price: cart?.unit_price,
            product: cart?.product,
            description: cart?.description,
            images: Array.isArray(cart?.images) ? cart?.images : `${cart?.images}`.split(","),
            batch: cart?.batch
          })),
          payment_mode: state?.payment_mode,
          payment_reference: state?.payment_reference,
          shipping: state?.shipping,
          batch: state?.batch
        }
      };
      startLoading();
      submitOrder(req).then((res) => {
        if (res?.data?.paid === true) {
          setPendingOrder({ id: res?.data?.id, paid: true, processed: true, completed: false });
          setOrderStatus(Order_Status.PAID);
        } else {
          console.log(res?.data);
          setPendingOrder({ id: res?.data?.id, paid: false, processed: false, completed: false });
          setOrderStatus(Order_Status.PENDING);
        }
      }).catch((err) => {
        console.error(err);
        toast.error("Fail to submit your order");
      }).finally(() => {
        stopLoading();
      });
    };
  }, [state]);

  const validateFormData = useMemo(() => {
    return () => {
      let hasError = false;
      Object.keys(formData).forEach(x => {
        if (!formData[x] || formData[x] === "" || `${formData[x]}`.match(/^(\+\d{1,3}[- ]?)?\d{10}$/ig) === null) {
          hasError = true;
          setErrors(p => ({ ...p, [x]: ["Valid mobile required"] }));
        }
      });
      if (hasError) {
        toast.error("Please fill required fields");
        scrollToTop(90);
      } else {
        paymentSubmit();
      }
    };
  }, [formData, paymentSubmit]);

  return (
    <>
      <div className="bg-white-A700 flex flex-col font-inter items-center justify-start mx-auto w-full">
        <div className="flex flex-col items-center justify-start w-full">
          <InfoRibbon className="bg-deep_purple-900 flex flex-row md:gap-10 items-center justify-between p-3 w-full" />
          <Header showSearch={false} />
          <div className="flex md:flex-col mb-16 flex-row gap-5 items-start justify-start mt-[58px] px-8 w-full">
            {
              orderStatus === Order_Status.NOT_PAID && <PaymentOptions
                errors={errors}
                formData={formData}
                setErrors={setErrors}
                setFormData={setFormData}
                validateFormData={validateFormData} />
            }
            {
              orderStatus === Order_Status.PAID &&
              <OrderPaidWidget order={pendingOrder?.paid === true ? { ...state, ...pendingOrder } : state} />
            }
            {
              orderStatus === Order_Status.PENDING &&
              <PendingPaymentWidget autoRetry={paymentSubmit} order={pendingOrder} />
            }
            <div
              className="bg-white-A700 border sticky top-[120px] flex md:flex-1 flex-col items-center justify-start p-[15px] rounded-lg w-[33%] md:w-full">
              <div className="flex flex-col gap-[18px] items-start justify-start w-full">
                <Text
                  className="text-black-900_dd text-xl font-bold tracking-[0.15px]"
                  size="txtInterRegular20"
                >
                  Total
                </Text>
                <div className="flex flex-col items-center justify-start w-[78%] md:w-full">
                  <div className="flex flex-row gap-3 items-start justify-start w-full">
                    <Text
                      className="text-blue_gray-700 text-xs tracking-[0.40px]"
                      size="txtInterRegular12"
                    >
                      TZS
                    </Text>
                    <Text
                      className="text-black-900_dd text-xl tracking-[0.15px]"
                      size="txtInterMedium20"
                    >
                      {formatCurrency(totalCartAmount)}
                    </Text>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Text className="text-xl font-bold text-gray-700 self-start px-8">Items</Text>
          {
            carts.length > 0 && <>
              <div className="flex flex-col gap-5 items-start justify-between mt-7 mx-auto px-8 w-full">
                <div
                  className="bg-white-A700 border flex md:flex-1 flex-col items-center justify-start p-[21px] sm:px-5 rounded-lg w-full">
                  <List
                    className="flex flex-col gap-8 items-center mt-[21px] w-full"
                    orientation="vertical"
                  >
                    {
                      carts?.map((cart, index) => {
                        return (
                          <Fragment key={cart?.id ?? index}>
                            <CartPreviewItem
                              quantity={cart?.quantity}
                              image={Array.isArray(cart?.images) ? cart?.images[0] : `${cart?.images}`.split(',')[0]}
                              amount={cart?.unit_price}
                              name={firstLetterUpper(cart?.product)}
                            />
                            {(index + 1) !== carts.length && <Line className="bg-gray-300 mb-8 h-px  mx-auto w-full" />}
                          </Fragment>
                        );
                      })
                    }
                  </List>
                </div>
                <div className="bg-white-A700 mt-8 flex md:flex-1 flex-col items-center justify-start rounded-lg w-full">
                  <div className="flex flex-col gap-[18px] items-start justify-start w-full">
                    <Text className="mb-3 text-lg font-bold">Shipping </Text>
                    <div className="w-full flex gap-4 flex-row sm:flex-col">
                      <FormInput label="Full name" className="w-full" value={state?.shipping?.fullname} />
                      <FormInput label="Email" className="w-full" value={state?.shipping?.email} />
                    </div>
                    <div className="w-full flex gap-4 flex-row sm:flex-col">
                      <FormInput label="Mobile" className="w-full" value={state?.shipping?.mobile} />
                      <FormInput label="Region" className="w-full" value={state?.shipping?.region} />
                    </div>
                    <FormInput label="Notes" lines={3} className="w-full" value={state?.shipping?.notes} />
                  </div>
                </div>
              </div>
            </>
          }
          <Footer1 className="bg-blue_gray-900 flex items-center justify-center mt-[104px] md:px-5 w-full" />
        </div>
      </div>
    </>
  );
};

export default OrderViewPage;
